import { modulesForPermission } from '../../assets/utils/accessCatalog';

/**
 * Shapes the permission catalog for browsing: module -> actions -> the
 * permission behind each action, the roles that grant it, and the screens it
 * opens.
 *
 * Until now this chain had no single place to stand. The catalog existed and
 * was populated, but it surfaced only as two dropdowns in the Create
 * Permission form and as grouping inside the role editor — so "which actions
 * does Facilities have, which of them are granted, and to whom" was not a
 * question the UI could answer. The data was all there; nothing joined it up.
 *
 * Note the deliberate separation kept throughout: a catalog MODULE is a
 * product area that owns capabilities (rbac_permission_modules), and a SCREEN
 * is a route unlocked by holding one of them (rbac_access_modules). They are
 * different axes with an unfortunately shared word. Screens appear here only
 * as a derived consequence of an action, never as something to grant.
 */

/** An action with no permission cannot be granted to anyone — it is a gap. */
export const actionState = (action) => {
  if (!action?.has_permission) return 'ungranted';
  if (action.permission_is_active === false) return 'inactive';
  return 'live';
};

export const ACTION_STATE_LABELS = {
  live: 'Has permission',
  inactive: 'Permission inactive',
  ungranted: 'No permission yet',
};

/**
 * @param modules  rbac_permission_modules rows
 * @param actions  rbac_module_actions rows (the whole catalog)
 * @param roleMap  permission name -> roles granting it, from listPermissionRoles
 * @returns one row per module, each carrying its decorated actions.
 */
export const buildCatalogRows = (modules = [], actions = [], roleMap = {}) => {
  const byModule = new Map();
  (actions || []).forEach((a) => {
    const key = a?.module_key;
    if (!key) return;
    if (!byModule.has(key)) byModule.set(key, []);
    byModule.get(key).push(decorateAction(a, roleMap));
  });

  const rows = [...(modules || [])]
    .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999)
      || String(a.label ?? '').localeCompare(String(b.label ?? '')))
    .map((m) => {
      const items = byModule.get(m.module_key) || [];
      return {
        key: m.module_key,
        module_key: m.module_key,
        label: m.label || m.module_key,
        description: m.description || '',
        is_system: Boolean(m.is_system),
        actions: items,
        action_count: items.length,
        granted_count: items.filter((a) => a.state !== 'ungranted').length,
        ungranted_count: items.filter((a) => a.state === 'ungranted').length,
        // An action with a permission that no role holds is live but unreachable —
        // worth surfacing separately from one with no permission at all.
        unheld_count: items.filter((a) => a.state !== 'ungranted' && a.roles.length === 0).length,
        role_count: new Set(items.flatMap((a) => a.roles.map((r) => r.id))).size,
      };
    });

  // A module whose actions were all removed still matters — it is where new
  // actions go. Keep it, rather than making it vanish from the only screen
  // that can add to it.
  return rows;
};

function decorateAction(action, roleMap) {
  const slug = action?.permission_slug
    || (action?.resource && action?.action ? `${action.resource}.${action.action}` : '');
  const roles = (roleMap && roleMap[slug]) || [];
  return {
    ...action,
    key: String(action?.id ?? slug),
    permission_slug: slug,
    state: actionState(action),
    roles,
    // Screens are derived from the permission, never the other way round.
    screens: slug ? modulesForPermission(slug) : [],
  };
}

/** Case- and separator-insensitive match across an action and its roles. */
export const matchesCatalogQuery = (action, query) => {
  if (!query) return true;
  const haystack = [
    action?.label,
    action?.permission_slug,
    action?.resource,
    action?.action,
    action?.description,
    ...(action?.roles || []).map((r) => r.display_name || r.name),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .replace(/[-_./]/g, ' ');
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => haystack.includes(token.replace(/[-_./]/g, ' ')));
};

/**
 * Permissions with no catalog action describing them.
 *
 * These are the drift: rows created directly in the database, or seeded
 * before the catalog existed. Wildcards are excluded — admin.* and platform.*
 * sit outside the catalog on purpose, so reporting them as gaps would train
 * everyone to ignore this list.
 */
export const uncataloguedPermissions = (permissions = [], actions = []) => {
  const catalogued = new Set(
    (actions || []).map((a) => a?.permission_slug
      || (a?.resource && a?.action ? `${a.resource}.${a.action}` : ''))
      .filter(Boolean)
  );
  return (permissions || []).filter((p) => {
    const name = String(p?.name || '');
    if (!name || name.includes('*')) return false;
    if (name.startsWith('platform.')) return false;
    return !catalogued.has(name);
  });
};
